import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  Zap, 
  Clock,
  DollarSign,
  Building,
  CheckCircle,
  AlertTriangle,
  Play,
  LogOut,
  ShieldAlert,
  KeyRound,
  Sparkles,
  Trash2,
  CreditCard
} from 'lucide-react';
import { apiService } from '../../services/api';
import EditorPdfSecao from './EditorPdfSecao';
import DiagnosticoPixCard from './DiagnosticoPixCard';
import PixDebitosCard from './PixDebitosCard';
import SafetyCobrancaCard from './SafetyCobrancaCard';
import NotificacoesPushCard from './NotificacoesPushCard';
import FerramentasCorrecaoCard from './FerramentasCorrecaoCard';

export const ConfiguracoesPage = () => {
  // Obter tipo do usuário do localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user?.tipo === 'super_admin';
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [config, setConfig] = useState({
    cnpj_empresa: '',
    consulta_automatica_ativa: true,
    intervalo_consulta_horas: 24,
    criar_cobranca_automatica: true,
    taxa_administrativa: 50.0,
    notificar_multas_novas: true
  });
  const [percentualDebitos, setPercentualDebitos] = useState(20);
  const [salvandoFinanceiro, setSalvandoFinanceiro] = useState(false);
  const [statusSistema, setStatusSistema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [desconectando, setDesconectando] = useState(false);
  const [executandoTeste, setExecutandoTeste] = useState(false);
  // Alteração de senha do super_admin
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');
  const [alterandoSenha, setAlterandoSenha] = useState(false);
  // Dados de demonstração — exibidos somente em ambiente de preview
  const isPreviewEnv = typeof window !== 'undefined'
    && window.location?.hostname?.includes('preview.emergentagent');
  const [demoStatus, setDemoStatus] = useState({ contagem: {}, total: 0, ativo: false });
  const [seedando, setSeedando] = useState(false);
  const [limpandoDemo, setLimpandoDemo] = useState(false);
  // Dados PIX — admin principal OU um sub-admin (selecionado no seletor)
  const [subAdmins, setSubAdmins] = useState([]);
  const [erroSubAdmins, setErroSubAdmins] = useState('');
  const [pixDestinatario, setPixDestinatario] = useState('padrao'); // 'padrao' | sub-admin id
  const [pixForm, setPixForm] = useState({ chave_pix: '', nome_beneficiario: '', nome_banco: '' });
  const [salvandoPix, setSalvandoPix] = useState(false);
  const [carregandoPix, setCarregandoPix] = useState(false);
  const [mostrarPreviewPix, setMostrarPreviewPix] = useState(false);

  useEffect(() => {
    carregarConfiguracoes();
    carregarStatusSistema();
    carregarConfigFinanceira();
    carregarSubAdmins();
    carregarPixDoSelecionado('padrao');
    if (isPreviewEnv) {
      carregarDemoStatus();
    }
  }, [isPreviewEnv]);

  const carregarSubAdmins = async () => {
    setErroSubAdmins('');
    try {
      const r = await apiService.listarUsuariosSistema();
      const lista = Array.isArray(r?.usuarios) ? r.usuarios : [];
      setSubAdmins(lista);
      if (lista.length === 0) {
        setErroSubAdmins('Nenhum sub-admin cadastrado. Vá em "Usuários" para criar sub-admins.');
      }
    } catch (e) {
      const detalhe = e.response?.data?.detail || e.message;
      setErroSubAdmins('Falha ao carregar sub-admins: ' + detalhe);
      setSubAdmins([]);
    }
  };

  const carregarPixDoSelecionado = async (alvo) => {
    setCarregandoPix(true);
    try {
      if (alvo === 'padrao') {
        const r = await apiService.api.get('/sistema/dados-cobranca-padrao');
        setPixForm({
          chave_pix: r.data?.chave_pix || '',
          nome_beneficiario: r.data?.nome_beneficiario || '',
          nome_banco: r.data?.nome_banco || ''
        });
      } else {
        // Sub-admin: busca da lista (já carregada com dados_cobranca)
        const lista = (subAdmins.length ? subAdmins : (await apiService.listarUsuariosSistema()).usuarios || []);
        const sub = lista.find(u => u.id === alvo);
        const dc = sub?.dados_cobranca || {};
        setPixForm({
          chave_pix: dc.chave_pix || '',
          nome_beneficiario: dc.nome_beneficiario || '',
          nome_banco: dc.nome_banco || ''
        });
      }
    } catch (e) {
      // mantém estado anterior
    } finally {
      setCarregandoPix(false);
    }
  };

  const onTrocarDestinatario = (alvo) => {
    setPixDestinatario(alvo);
    carregarPixDoSelecionado(alvo);
  };

  const salvarPixDoSelecionado = async () => {
    const chave = pixForm.chave_pix.trim();
    const benef = pixForm.nome_beneficiario.trim();
    const banco = pixForm.nome_banco.trim();
    const isSub = pixDestinatario !== 'padrao';

    if (isSub) {
      // Para sub-admin permitimos os 3 vazios (= remover override e voltar a usar o padrão)
      if ((chave || benef || banco) && (!chave || !benef || !banco)) {
        alert('Para o sub-admin, preencha os 3 campos OU deixe os 3 em branco para voltar ao PIX padrão.');
        return;
      }
    } else {
      if (!chave || !benef || !banco) {
        alert('Preencha os 3 campos: chave PIX, nome do beneficiário e nome do banco.');
        return;
      }
    }

    setSalvandoPix(true);
    try {
      if (isSub) {
        await apiService.atualizarUsuarioSistema(pixDestinatario, {
          dados_cobranca: { chave_pix: chave, nome_beneficiario: benef, nome_banco: banco }
        });
        await carregarSubAdmins();
        alert(chave ? 'PIX do sub-admin salvo!' : 'PIX do sub-admin removido — passa a usar o PIX padrão.');
      } else {
        await apiService.api.put('/sistema/dados-cobranca-padrao', {
          chave_pix: chave, nome_beneficiario: benef, nome_banco: banco
        });
        alert('PIX padrão salvo!');
      }
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSalvandoPix(false);
    }
  };

  const carregarDemoStatus = async () => {
    try {
      const r = await apiService.api.get('/admin/seed-demo/status');
      setDemoStatus(r.data || { contagem: {}, total: 0, ativo: false });
    } catch (e) {
      // silencioso — endpoint disponível apenas no preview/super_admin
    }
  };

  const seedarDemo = async () => {
    if (!window.confirm('Criar dados FICTÍCIOS de demonstração? (motos, clientes, locações, pagamentos)')) return;
    setSeedando(true);
    try {
      const r = await apiService.api.post('/admin/seed-demo');
      alert(`Dados de demonstração criados!\n\n` +
        Object.entries(r.data.criados).map(([k,v]) => `• ${v} ${k}`).join('\n'));
      await carregarDemoStatus();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSeedando(false);
    }
  };

  const limparDemo = async () => {
    if (!window.confirm('Remover TODOS os dados de demonstração (marcados como _demo)? Dados reais não serão tocados.')) return;
    setLimpandoDemo(true);
    try {
      const r = await apiService.api.delete('/admin/seed-demo');
      const remov = Object.entries(r.data.removidos || {});
      alert(remov.length
        ? `Removidos:\n${remov.map(([k,v]) => `• ${v} ${k}`).join('\n')}`
        : 'Nenhum registro de demo encontrado para remover.');
      await carregarDemoStatus();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLimpandoDemo(false);
    }
  };

  const carregarConfigFinanceira = async () => {
    try {
      const r = await apiService.api.get('/sistema/config-financeira');
      setPercentualDebitos(r.data?.percentual_debitos_receita ?? 20);
    } catch (e) {
      // mantém default 20
    }
  };

  const salvarConfigFinanceira = async () => {
    const v = parseFloat(percentualDebitos);
    if (isNaN(v) || v < 0 || v > 100) {
      alert('Informe um percentual entre 0 e 100');
      return;
    }
    setSalvandoFinanceiro(true);
    try {
      await apiService.api.put('/sistema/config-financeira', { percentual_debitos_receita: v });
      alert('Configuração financeira salva com sucesso!');
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSalvandoFinanceiro(false);
    }
  };

  const carregarConfiguracoes = async () => {
    try {
      const configData = await apiService.getConfigDetran();
      setConfig(configData);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const carregarStatusSistema = async () => {
    try {
      const statusData = await apiService.getStatusSistema();
      setStatusSistema(statusData);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const salvarConfiguracoes = async () => {
    setSalvando(true);
    try {
      await apiService.updateConfigDetran(config);
      alert('Configurações salvas com sucesso!');
      await carregarStatusSistema(); // Atualizar status
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const executarTesteConsulta = async () => {
    setExecutandoTeste(true);
    try {
      await apiService.executarConsultaDetranAgora();
      alert('Teste de consulta DETRAN executado com sucesso!');
      await carregarStatusSistema();
    } catch (error) {
      console.error('Erro no teste:', error);
      alert('Erro ao executar teste. Verifique os logs.');
    } finally {
      setExecutandoTeste(false);
    }
  };

  const formatarProximaExecucao = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Carregando configurações...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          Configurações do Sistema
        </h2>
        
        <div className="flex space-x-2">
          {!isSubAdmin && (
            <Button 
              onClick={executarTesteConsulta}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={executandoTeste}
            >
              <Play className="h-4 w-4 mr-2" />
              {executandoTeste ? 'Executando...' : 'Testar DETRAN'}
            </Button>
          )}
        </div>
      </div>

      {/* Notificações Push — apenas super_admin */}
      <NotificacoesPushCard isSuperAdmin={isSuperAdmin} />

      {/* Ferramentas de Correção — só super_admin (sub-admin não corrige dados globais) */}
      {!isSubAdmin && <FerramentasCorrecaoCard />}

      {/* Configuração Financeira (super_admin) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              <span>Configuração Financeira</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="perc-debitos">Percentual de débitos como receita do administrador (%)</Label>
              <div className="flex gap-2 items-center mt-1 max-w-md">
                <Input
                  data-testid="input-perc-debitos"
                  id="perc-debitos"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={percentualDebitos}
                  onChange={(e) => setPercentualDebitos(e.target.value)}
                  className="max-w-[120px]"
                />
                <span className="text-sm text-gray-500">% do valor bruto recebido em débitos</span>
                <Button
                  data-testid="btn-salvar-perc-debitos"
                  onClick={salvarConfigFinanceira}
                  disabled={salvandoFinanceiro}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {salvandoFinanceiro ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Define quanto dos <strong>débitos/cobranças extras</strong> (multas de trânsito, taxas, etc) entra como receita/lucro no Fechamento Mensal.
                O restante é considerado ressarcimento de gasto (neutro).
                <br/>
                <strong className="text-gray-600">Importante:</strong> multas e juros por atraso de pagamento NÃO são afetados — sempre contam 100% como receita.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dados de Cobrança PIX — Padrão + cada sub-admin (super_admin) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-blue-500" />
              <span>Dados de Cobrança PIX</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">
              Selecione <strong>Admin Principal (Padrão)</strong> para definir o PIX que aparece nas locações sem
              compartilhamento. Selecione um <strong>sub-admin</strong> para definir o PIX dele, que será usado
              automaticamente em todas as locações compartilhadas com aquele sub-admin.
            </p>

            <div>
              <Label htmlFor="pix-destinatario">Configurar PIX para</Label>
              <select
                id="pix-destinatario"
                data-testid="select-pix-destinatario"
                value={pixDestinatario}
                onChange={(e) => onTrocarDestinatario(e.target.value)}
                className="w-full border rounded px-3 py-2 bg-white text-gray-900"
              >
                <option value="padrao">Admin Principal (Padrão)</option>
                {subAdmins.map(u => {
                  const temPix = !!u.dados_cobranca?.chave_pix;
                  const inativo = u.ativo === false;
                  const sufixo = inativo
                    ? ' — INATIVO'
                    : (temPix ? ' — PIX próprio' : ' — usa padrão');
                  return (
                    <option key={u.id} value={u.id}>
                      Sub-admin: {u.nome_completo || u.username}{sufixo}
                    </option>
                  );
                })}
              </select>
              {erroSubAdmins && (
                <p className="text-xs text-amber-600 mt-1" data-testid="aviso-sub-admins">
                  ⚠️ {erroSubAdmins}
                </p>
              )}
            </div>

            {carregandoPix ? (
              <p className="text-sm text-gray-500 italic">Carregando dados...</p>
            ) : (
              <>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label>Chave PIX</Label>
                    <Input
                      data-testid="input-pix-chave"
                      placeholder={pixDestinatario === 'padrao' ? 'CNPJ, CPF, email ou aleatória' : 'Deixe vazio para usar o padrão'}
                      value={pixForm.chave_pix}
                      onChange={(e) => setPixForm({ ...pixForm, chave_pix: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Nome do Beneficiário</Label>
                    <Input
                      data-testid="input-pix-beneficiario"
                      placeholder="Ex.: Carlos Silva"
                      value={pixForm.nome_beneficiario}
                      onChange={(e) => setPixForm({ ...pixForm, nome_beneficiario: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Nome do Banco</Label>
                    <Input
                      data-testid="input-pix-banco"
                      placeholder="Ex.: Nubank, Bradesco, Itaú"
                      value={pixForm.nome_banco}
                      onChange={(e) => setPixForm({ ...pixForm, nome_banco: e.target.value })}
                    />
                  </div>
                </div>
                {pixDestinatario !== 'padrao' && (
                  <p className="text-xs text-gray-500">
                    💡 Deixe os 3 campos em branco e clique em <strong>Salvar</strong> para remover o PIX próprio
                    deste sub-admin e voltar a usar o PIX padrão nas locações dele.
                  </p>
                )}

                {mostrarPreviewPix && (
                  <div className="rounded-lg border border-green-500/20 bg-green-50 dark:bg-green-900/10 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-green-700 dark:text-green-300">
                        Pré-visualização do WhatsApp
                      </div>
                      <button
                        type="button"
                        onClick={() => setMostrarPreviewPix(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        data-testid="btn-fechar-preview-pix"
                      >
                        Fechar
                      </button>
                    </div>
                    <pre
                      data-testid="preview-pix-mensagem"
                      className="text-xs whitespace-pre-wrap font-mono text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900 p-3 rounded border"
                    >{`🏍️ *DESLOCA - Lembrete de Pagamento*

Olá João da Silva!

💰 Seu pagamento semanal vence em: *${new Date(Date.now()+2*86400000).toLocaleDateString('pt-BR')}*
📊 Valor: *R$ 350,00*
🏍️ Moto: *ABC1D23*
📅 Semana: *3*

💳 *DADOS PARA PAGAMENTO:*
• *PIX${(() => {
  const chave = pixForm.chave_pix.trim();
  if (!chave) return '';
  const digits = chave.replace(/\D/g, '');
  if (chave.includes('@')) return ' (E-mail)';
  if (digits.length === 14) return ' (CNPJ)';
  if (digits.length === 11) {
    if (chave.includes('(') || chave.includes(')') || chave.startsWith('+')) return ' (Telefone)';
    return ' (CPF)';
  }
  if (digits.length === 10 || digits.length === 12 || digits.length === 13 || chave.startsWith('+')) return ' (Telefone)';
  return ' (Aleatória)';
})()}:* ${pixForm.chave_pix || '(em branco — usará padrão)'}
• *Banco:* ${pixForm.nome_banco || '(em branco — usará padrão)'}
• *Nome:* ${pixForm.nome_beneficiario || '(em branco — usará padrão)'}

💡 Após efetuar o pagamento, envie o comprovante para confirmar.

---
_Mensagem automática - Desloca Locação | Atendimento 24h_`}</pre>
                    <p className="text-xs text-gray-500 mt-2">
                      Cliente, valor, moto e data são exemplos. PIX usa os dados que você está editando agora.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    data-testid="btn-preview-pix"
                    type="button"
                    onClick={() => setMostrarPreviewPix(v => !v)}
                    variant="outline"
                  >
                    {mostrarPreviewPix ? 'Ocultar pré-visualização' : 'Pré-visualizar mensagem'}
                  </Button>
                  <Button
                    data-testid="btn-salvar-pix"
                    onClick={salvarPixDoSelecionado}
                    disabled={salvandoPix}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {salvandoPix ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* 🔑 PIX dos Débitos — DESTAQUE: aparece logo após o PIX de aluguel
           para ficar fácil de configurar a chave separada. */}
      {isSuperAdmin && (
        <div id="pix-debitos-config" className="ring-2 ring-orange-500/30 rounded-lg">
          <PixDebitosCard />
        </div>
      )}

      {/* Safety net de cobrança recorrente (super_admin) */}
      {isSuperAdmin && (
        <SafetyCobrancaCard />
      )}

      {/* Diagnóstico PIX por Locação (super_admin) */}
      {isSuperAdmin && (
        <DiagnosticoPixCard />
      )}

      {/* Configurações da Empresa */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-blue-500" />
            <span>Dados da Empresa</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="cnpj">CNPJ da Desloca</Label>
            <Input
              id="cnpj"
              value={config.cnpj_empresa}
              onChange={(e) => setConfig({...config, cnpj_empresa: e.target.value})}
              placeholder="00.000.000/0001-00"
              maxLength={18}
              disabled={isSubAdmin}
            />
            <p className="text-sm text-gray-500 mt-1">
              CNPJ usado para consultas no DETRAN-SP
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Configurações DETRAN */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-purple-500" />
            <span>Consulta Automática DETRAN-SP</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="consulta_ativa"
              checked={config.consulta_automatica_ativa}
              onChange={(e) => setConfig({...config, consulta_automatica_ativa: e.target.checked})}
              className="w-4 h-4 text-blue-600"
              disabled={isSubAdmin}
            />
            <Label htmlFor="consulta_ativa" className="font-medium">
              Ativar consulta automática diária
            </Label>
          </div>

          <div>
            <Label htmlFor="intervalo">Intervalo de Consulta (horas)</Label>
            <Input
              id="intervalo"
              type="number"
              value={config.intervalo_consulta_horas}
              onChange={(e) => setConfig({...config, intervalo_consulta_horas: parseInt(e.target.value)})}
              min="1"
              max="168"
              disabled={isSubAdmin}
            />
            <p className="text-sm text-gray-500 mt-1">
              Padrão: 24 horas (consulta diária às 2h da manhã)
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="cobranca_automatica"
              checked={config.criar_cobranca_automatica}
              onChange={(e) => setConfig({...config, criar_cobranca_automatica: e.target.checked})}
              className="w-4 h-4 text-blue-600"
              disabled={isSubAdmin}
            />
            <Label htmlFor="cobranca_automatica" className="font-medium">
              Criar cobrança automática para multas
            </Label>
          </div>

          <div>
            <Label htmlFor="taxa">Taxa Administrativa (R$)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="taxa"
                type="number"
                value={config.taxa_administrativa}
                onChange={(e) => setConfig({...config, taxa_administrativa: parseFloat(e.target.value)})}
                className="pl-10"
                min="0"
                step="0.01"
                disabled={isSubAdmin}
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Taxa administrativa adicionada ao valor da multa
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="notificar_multas"
              checked={config.notificar_multas_novas}
              onChange={(e) => setConfig({...config, notificar_multas_novas: e.target.checked})}
              className="w-4 h-4 text-blue-600"
              disabled={isSubAdmin}
            />
            <Label htmlFor="notificar_multas" className="font-medium">
              Notificar sobre multas novas
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        {!isSubAdmin && (
          <Button 
            data-testid="btn-salvar-config"
            onClick={salvarConfiguracoes}
            className="bg-green-600 hover:bg-green-700"
            disabled={salvando}
          >
            <Save className="h-4 w-4 mr-2" />
            {salvando ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        )}
      </div>

      {/* Dados de Demonstração (super_admin, somente preview) */}
      {isSuperAdmin && isPreviewEnv && (
        <Card className="bg-slate-800 border-indigo-500/20 mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-indigo-300">
              <Sparkles className="h-5 w-5" />
              Dados de Demonstração
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-400">
              Gera um conjunto de dados <strong className="text-white">fictícios</strong> (motos, clientes,
              locações, pagamentos e cobranças extras) marcados como <code className="text-indigo-300">_demo</code>{' '}
              para usar em apresentações comerciais sem expor dados reais.
              A limpeza remove <strong>apenas</strong> registros tagueados, jamais dados reais.
            </p>

            <div className="flex flex-wrap gap-3 items-center">
              <Badge variant="outline" className="border-indigo-500/30 text-indigo-300">
                Demo atual: {demoStatus.total} registros
              </Badge>
              {demoStatus.total > 0 && (
                <span className="text-xs text-gray-500">
                  {Object.entries(demoStatus.contagem || {})
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => `${v} ${k}`)
                    .join(' · ')}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="btn-seedar-demo"
                onClick={seedarDemo}
                disabled={seedando || demoStatus.ativo}
                className="bg-indigo-600 hover:bg-indigo-500 text-white"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {seedando ? 'Criando...' : 'Criar dados de demonstração'}
              </Button>
              <Button
                data-testid="btn-limpar-demo"
                onClick={limparDemo}
                disabled={limpandoDemo || demoStatus.total === 0}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {limpandoDemo ? 'Limpando...' : 'Limpar dados de demonstração'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Segurança — Desconectar Dispositivos */}
      {isSuperAdmin && (
        <Card className="bg-slate-800 border-red-500/20 mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <ShieldAlert className="h-5 w-5" />
              Segurança
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <h4 className="font-semibold text-white mb-1 flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Alterar minha senha
              </h4>
              <p className="text-sm text-gray-400 mb-4">
                Troca a senha do super-admin. Após salvar, todos os dispositivos serão desconectados e precisarão entrar de novo.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-gray-300 text-xs">Senha atual</Label>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    className="bg-slate-700 border-white/10 text-white"
                    data-testid="input-senha-atual"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Nova senha</Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    className="bg-slate-700 border-white/10 text-white"
                    data-testid="input-nova-senha"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Confirmar nova senha</Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={confirmaSenha}
                    onChange={(e) => setConfirmaSenha(e.target.value)}
                    className="bg-slate-700 border-white/10 text-white"
                    data-testid="input-confirma-senha"
                  />
                </div>
              </div>
              <Button
                data-testid="btn-alterar-senha"
                onClick={async () => {
                  if (!senhaAtual || !novaSenha) { alert('Preencha senha atual e nova senha.'); return; }
                  if (novaSenha.length < 6) { alert('Nova senha precisa ter pelo menos 6 caracteres.'); return; }
                  if (novaSenha !== confirmaSenha) { alert('A confirmação não bate com a nova senha.'); return; }
                  if (!window.confirm('Confirmar alteração? Todos os dispositivos serão deslogados.')) return;
                  setAlterandoSenha(true);
                  try {
                    await apiService.api.post('/auth/super-admin/alterar-senha', {
                      senha_atual: senhaAtual,
                      nova_senha: novaSenha
                    });
                    alert('Senha alterada com sucesso! Faça login novamente.');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    window.location.href = '/admin/login';
                  } catch (error) {
                    alert('Erro: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setAlterandoSenha(false);
                  }
                }}
                disabled={alterandoSenha}
                className="mt-3 bg-blue-600 hover:bg-blue-500 text-white"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                {alterandoSenha ? 'Alterando...' : 'Alterar senha'}
              </Button>
            </div>

            <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <h4 className="font-semibold text-white mb-1">Desconectar Todos os Dispositivos</h4>
              <p className="text-sm text-gray-400 mb-4">
                Remove o acesso de todos os dispositivos conectados ao sistema. Todos os usuários (incluindo você) precisarão fazer login novamente.
              </p>
              <Button
                data-testid="btn-desconectar-todos"
                onClick={async () => {
                  if (!window.confirm('Tem certeza? TODOS os dispositivos serão desconectados e precisarão fazer login novamente.')) return;
                  setDesconectando(true);
                  try {
                    await apiService.api.post('/auth/desconectar-todos');
                    alert('Todos os dispositivos foram desconectados. Você será redirecionado para o login.');
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/admin/login';
                  } catch (error) {
                    alert('Erro: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setDesconectando(false);
                  }
                }}
                disabled={desconectando}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {desconectando ? 'Desconectando...' : 'Desconectar Todos'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
